/**
 * Domaines EQUIVALENTS — CDC § « Reconnaitre le maximum d'applications et de
 * sites » : une banque qui authentifie ailleurs que sur sa vitrine
 * (`societegenerale.fr` → `particuliers.sg.fr`), un groupe qui partage un
 * compte (`youtube.com` → `accounts.google.com`).
 *
 * La liste est SOURCEE et VERSIONNEE, jamais deduite : chaque groupe porte sa
 * source, et rien ici ne rapproche deux domaines parce qu'ils « se
 * ressemblent ». Deux garde-fous tiennent la faute la plus grave du domaine
 * (remplir sur un site qui ne correspond pas) :
 *   - un membre de groupe doit etre un DOMAINE ENREGISTRABLE (eTLD+1) : jamais
 *     un suffixe public (`net.cn`, `github.io`), jamais un sous-domaine — la
 *     liste est refusee au chargement sinon ;
 *   - une entree peut s'en RETIRER (`secrets.equivalentDomains === false`) :
 *     elle ne correspond alors qu'a son propre site. Le choix vit dans les
 *     secrets, chiffre : le serveur n'en sait rien.
 */
import { isSameSite, registrableDomain, siteHostname } from './domain.js';
import { SHARED_CREDENTIALS, type SharedCredentialsRecord } from './shared-credentials-data.js';

export interface EquivalentDomainGroup {
  readonly id: string;
  /** D'ou vient le groupe : sans source, pas de groupe. */
  readonly source: string;
  readonly domains: readonly string[];
}

/** Liste de domaines equivalents de Bitwarden (AGPL-3.0), transcrite a ce commit. */
const BITWARDEN = 'bitwarden/server src/Core/Utilities/StaticStore.cs @ e1f32b96a33f (2026-09-29)';
/** Redirection de connexion observee par KLAVOX (`curl -sIL <vitrine>`), datee. */
const OBSERVED = (from: string, to: string): string => `observe le 2026-10-01 : ${from} redirige vers ${to}`;

export const EQUIVALENT_DOMAINS_VERSION = '2026-10-01';

export const EQUIVALENT_DOMAIN_GROUPS: readonly EquivalentDomainGroup[] = [
  { id: 'google', source: BITWARDEN, domains: ['youtube.com', 'google.com', 'gmail.com'] },
  { id: 'apple', source: BITWARDEN, domains: ['apple.com', 'icloud.com'] },
  { id: 'merrill', source: BITWARDEN, domains: ['mymerrill.com', 'ml.com', 'merrilledge.com'] },
  {
    id: 'microsoft',
    source: BITWARDEN,
    domains: [
      'bing.com', 'hotmail.com', 'live.com', 'microsoft.com', 'msn.com', 'passport.net', 'windows.com',
      'microsoftonline.com', 'office.com', 'office365.com', 'microsoftstore.com', 'xbox.com', 'azure.com',
      'windowsazure.com', 'cloud.microsoft',
    ],
  },
  { id: 'paypal', source: BITWARDEN, domains: ['paypal.com', 'paypal-search.com'] },
  // `amazon.com.be` figure dans la source, mais `com.be` n'est PAS un suffixe
  // public : son domaine enregistrable est `com.be`, et le garder aurait range
  // TOUT site en `.com.be` dans le groupe Amazon. Retire.
  {
    id: 'amazon',
    source: `${BITWARDEN}, sans amazon.com.be (domaine enregistrable : com.be)`,
    domains: [
      'amazon.com', 'amazon.ae', 'amazon.ca', 'amazon.co.uk', 'amazon.com.au', 'amazon.com.br',
      'amazon.com.mx', 'amazon.com.tr', 'amazon.de', 'amazon.es', 'amazon.fr', 'amazon.in', 'amazon.it', 'amazon.nl',
      'amazon.pl', 'amazon.sa', 'amazon.se', 'amazon.sg',
    ],
  },
  { id: 'comcast', source: BITWARDEN, domains: ['comcast.com', 'comcast.net', 'xfinity.com'] },
  // `net.cn` figure dans la source : c'est un SUFFIXE PUBLIC (tous les sites
  // en `.net.cn` deviendraient Alibaba). Retire, et c'est le chargement qui le
  // refuserait s'il revenait.
  { id: 'alibaba', source: `${BITWARDEN}, sans net.cn (suffixe public)`, domains: ['alibaba.com', 'aliexpress.com', 'aliyun.com'] },
  { id: 'autodesk', source: BITWARDEN, domains: ['autodesk.com', 'tinkercad.com'] },
  { id: 'facebook', source: BITWARDEN, domains: ['facebook.com', 'messenger.com'] },
  { id: 'disney', source: BITWARDEN, domains: ['disneymoviesanywhere.com', 'go.com', 'disney.com', 'dadt.com', 'disneyplus.com'] },
  { id: 'turbotax', source: BITWARDEN, domains: ['turbotax.com', 'intuit.com'] },
  {
    id: 'ebay',
    source: BITWARDEN,
    domains: [
      'ebay.com', 'ebay.at', 'ebay.be', 'ebay.ca', 'ebay.ch', 'ebay.cn', 'ebay.co.jp', 'ebay.co.th', 'ebay.co.uk',
      'ebay.com.au', 'ebay.com.hk', 'ebay.com.my', 'ebay.com.sg', 'ebay.com.tw', 'ebay.de', 'ebay.es', 'ebay.fr',
      'ebay.ie', 'ebay.in', 'ebay.it', 'ebay.nl', 'ebay.ph', 'ebay.pl',
    ],
  },
  { id: 'morganstanley', source: BITWARDEN, domains: ['morganstanley.com', 'morganstanleyclientserv.com', 'stockplanconnect.com', 'ms.com'] },
  {
    id: 'airbnb',
    source: BITWARDEN,
    domains: [
      'airbnb.at', 'airbnb.be', 'airbnb.ca', 'airbnb.ch', 'airbnb.cl', 'airbnb.co.cr', 'airbnb.co.id', 'airbnb.co.in',
      'airbnb.co.kr', 'airbnb.co.nz', 'airbnb.co.uk', 'airbnb.co.ve', 'airbnb.com', 'airbnb.com.ar', 'airbnb.com.au',
      'airbnb.com.bo', 'airbnb.com.br', 'airbnb.com.bz', 'airbnb.com.co', 'airbnb.com.ec', 'airbnb.com.gt',
      'airbnb.com.hk', 'airbnb.com.hn', 'airbnb.com.mt', 'airbnb.com.my', 'airbnb.com.ni', 'airbnb.com.pa',
      'airbnb.com.pe', 'airbnb.com.py', 'airbnb.com.sg', 'airbnb.com.sv', 'airbnb.com.tr', 'airbnb.com.tw', 'airbnb.cz',
      'airbnb.de', 'airbnb.dk', 'airbnb.es', 'airbnb.fi', 'airbnb.fr', 'airbnb.gr', 'airbnb.gy', 'airbnb.hu',
      'airbnb.ie', 'airbnb.is', 'airbnb.it', 'airbnb.jp', 'airbnb.mx', 'airbnb.nl', 'airbnb.no', 'airbnb.pl',
      'airbnb.pt', 'airbnb.ru', 'airbnb.se',
    ],
  },
  {
    id: 'stackexchange',
    source: BITWARDEN,
    domains: ['stackexchange.com', 'superuser.com', 'stackoverflow.com', 'serverfault.com', 'mathoverflow.net', 'askubuntu.com', 'stackapps.com'],
  },
  { id: 'docusign', source: BITWARDEN, domains: ['docusign.com', 'docusign.net'] },
  { id: 'ubiquiti', source: BITWARDEN, domains: ['ubnt.com', 'ui.com'] },
  {
    id: 'yandex',
    source: BITWARDEN,
    domains: [
      'yandex.com', 'ya.ru', 'yandex.az', 'yandex.by', 'yandex.co.il', 'yandex.com.am', 'yandex.com.ge', 'yandex.com.tr',
      'yandex.ee', 'yandex.fi', 'yandex.fr', 'yandex.kg', 'yandex.kz', 'yandex.lt', 'yandex.lv', 'yandex.md', 'yandex.pl',
      'yandex.ru', 'yandex.tj', 'yandex.tm', 'yandex.ua', 'yandex.uz',
    ],
  },
  { id: 'proton', source: BITWARDEN, domains: ['proton.me', 'protonmail.com', 'protonvpn.com'] },
  { id: 'atlassian', source: BITWARDEN, domains: ['atlassian.com', 'bitbucket.org', 'trello.com', 'statuspage.io', 'atlassian.net', 'jira.com'] },
  { id: 'twitter', source: BITWARDEN, domains: ['twitter.com', 'x.com'] },
  // Groupes europeens absents de la source ci-dessus, chacun sur une redirection observee.
  { id: 'bbc', source: OBSERVED('https://www.bbc.co.uk/signin', 'account.bbc.com'), domains: ['bbc.co.uk', 'bbc.com'] },
  { id: 'societegenerale', source: OBSERVED('https://www.societegenerale.fr/', 'particuliers.sg.fr'), domains: ['societegenerale.fr', 'sg.fr'] },
  { id: 'decathlon', source: OBSERVED('https://www.decathlon.fr/login', 'login.decathlon.net'), domains: ['decathlon.fr', 'decathlon.net'] },
  { id: 'uber', source: OBSERVED('https://www.ubereats.com/login-redirect', 'auth.uber.com'), domains: ['uber.com', 'ubereats.com'] },
];

export class InvalidEquivalentDomainsError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'InvalidEquivalentDomainsError';
  }
}

/**
 * Index « domaine enregistrable → groupe ». Refuse une liste qui rapprocherait
 * sur un suffixe public, nommerait un sous-domaine, un groupe sans source, ou
 * un domaine present dans deux groupes (l'equivalence deviendrait transitive
 * sans que personne ne l'ait decide).
 */
export function indexEquivalentDomains(groups: readonly EquivalentDomainGroup[]): ReadonlyMap<string, string> {
  const index = new Map<string, string>();
  for (const group of groups) {
    if (group.source.trim() === '') throw new InvalidEquivalentDomainsError(`groupe ${group.id} : source absente`);
    if (group.domains.length < 2) throw new InvalidEquivalentDomainsError(`groupe ${group.id} : moins de deux domaines`);
    for (const domain of group.domains) {
      if (registrableDomain(domain) !== domain) {
        throw new InvalidEquivalentDomainsError(
          `groupe ${group.id} : « ${domain} » n'est pas un domaine enregistrable (suffixe public ou sous-domaine)`,
        );
      }
      const other = index.get(domain);
      if (other !== undefined) throw new InvalidEquivalentDomainsError(`« ${domain} » est dans ${other} et ${group.id}`);
      index.set(domain, group.id);
    }
  }
  return index;
}

let defaultIndex: ReadonlyMap<string, string> | null = null;

function groupOf(uri: string, index: ReadonlyMap<string, string>): string | undefined {
  const host = siteHostname(uri);
  const site = host === null ? null : registrableDomain(host);
  return site === null ? undefined : index.get(site);
}

/** Un sens de la table d'Apple, membres valides seulement : un identifiant de `from` vaut sur `to`. */
interface SharedCredentialsLink {
  readonly from: readonly string[];
  readonly to: readonly string[];
  /** `fromDomainsAreObsoleted` d'Apple : le service a quitte les domaines `from` pour `to`. */
  readonly obsolete: boolean;
}

export interface SharedCredentialsIndex {
  /** Domaine enregistrable → sens dont un membre `from` y vit (recherche par adresse, pas un parcours). */
  readonly bySite: ReadonlyMap<string, readonly SharedCredentialsLink[]>;
  /** Membres ecartes au chargement : ils ne designent aucun site (suffixe public, illisible). */
  readonly setAside: readonly string[];
}

/**
 * Un membre de la table d'Apple designe-t-il cet hote ? Un domaine enregistrable vaut pour tout son site ; un
 * sous-domaine (`signin.costco.com`, `id.npo.nl`) pour lui-meme et ce qu'il contient, jamais pour le reste du site.
 */
function memberMatchesHost(member: string, host: string): boolean {
  return registrableDomain(member) === member ? registrableDomain(host) === member : host === member || host.endsWith(`.${member}`);
}

/**
 * Index de la table d'Apple. `shared` vaut dans les deux sens (`from` = `to`), `from` / `to` dans un seul. Un membre
 * qui n'est sous aucun domaine enregistrable (suffixe public : `github.io`, `co.uk`) est ECARTE, jamais elargi ; un
 * groupe reduit a un domaine, ou un sens sans depart ni arrivee, disparait avec lui.
 */
export function indexSharedCredentials(records: readonly SharedCredentialsRecord[]): SharedCredentialsIndex {
  const bySite = new Map<string, SharedCredentialsLink[]>();
  const setAside: string[] = [];
  const valid = (domains: readonly string[]): string[] =>
    domains.filter((domain) => {
      const ok = siteHostname(domain) === domain && registrableDomain(domain) !== null;
      if (!ok) setAside.push(domain);
      return ok;
    });
  for (const record of records) {
    const link: SharedCredentialsLink =
      'shared' in record
        ? { from: valid(record.shared), to: [], obsolete: false }
        : { from: valid(record.from), to: valid(record.to), obsolete: record.obsolete === true };
    const resolved: SharedCredentialsLink = 'shared' in record ? { from: link.from, to: link.from, obsolete: false } : link;
    if (resolved.from.length === 0 || resolved.to.length === 0 || ('shared' in record && resolved.from.length < 2)) continue;
    for (const site of new Set(resolved.from.map((domain) => registrableDomain(domain)!))) {
      const links = bySite.get(site);
      if (links === undefined) bySite.set(site, [resolved]);
      else links.push(resolved);
    }
  }
  return { bySite, setAside };
}

let defaultSharedIndex: SharedCredentialsIndex | null = null;

/** Index de la table d'Apple embarquee (charge une fois). */
export function sharedCredentialsIndex(): SharedCredentialsIndex {
  return (defaultSharedIndex ??= indexSharedCredentials(SHARED_CREDENTIALS));
}

/** Sens de la table d'Apple dont un membre `from` designe l'hote de cette adresse. */
function linksFrom(uri: string, index: SharedCredentialsIndex): readonly SharedCredentialsLink[] {
  const host = siteHostname(uri);
  if (host === null) return [];
  // Hors de tout domaine enregistrable (IP, suffixe public) : aucune cle, aucun sens.
  const links = index.bySite.get(registrableDomain(host) ?? '') ?? [];
  return links.filter((link) => link.from.some((member) => memberMatchesHost(member, host)));
}

/**
 * Les AUTRES domaines ou un identifiant de cette adresse est reconnu : son groupe de la liste ci-dessus, puis la table
 * d'Apple (groupes et sens uniques au depart de ce site). Vide hors groupe. Sert a l'IHM a dire ce que la case
 * d'opt-out retire, et a la Sante du coffre a chercher le site sous ses autres noms.
 */
export function equivalentDomainsOf(uri: string): readonly string[] {
  const index = (defaultIndex ??= indexEquivalentDomains(EQUIVALENT_DOMAIN_GROUPS));
  const id = groupOf(uri, index);
  const group = id === undefined ? undefined : EQUIVALENT_DOMAIN_GROUPS.find((candidate) => candidate.id === id);
  const others = new Set<string>();
  for (const domain of [...(group?.domains ?? []), ...linksFrom(uri, sharedCredentialsIndex()).flatMap((link) => link.to)]) {
    // Ce que l'adresse reconnait deja (son site, ou un sous-domaine de son site) n'est pas un « autre » domaine.
    if (!isSameSite(`https://${domain}`, uri)) others.add(domain);
  }
  return [...others];
}

/**
 * Meme site, deux sites d'un meme groupe equivalent, ou -- table d'Apple -- un identifiant de `uriA` (l'adresse de
 * l'entree) qui vaut sur `uriB` (la page) : un sens unique `from` → `to` ne vaut jamais a rebours. `groups` ne sert
 * qu'aux tests ; le coffre utilise la liste embarquee, et une liste de test remplace aussi la table d'Apple.
 */
export function isEquivalentSite(
  uriA: string,
  uriB: string,
  groups: readonly EquivalentDomainGroup[] = EQUIVALENT_DOMAIN_GROUPS,
): boolean {
  if (isSameSite(uriA, uriB)) return true;
  const index =
    groups === EQUIVALENT_DOMAIN_GROUPS ? (defaultIndex ??= indexEquivalentDomains(groups)) : indexEquivalentDomains(groups);
  const group = groupOf(uriA, index);
  if (group !== undefined && group === groupOf(uriB, index)) return true;
  if (groups !== EQUIVALENT_DOMAIN_GROUPS) return false;
  const pageHost = siteHostname(uriB);
  return (
    pageHost !== null &&
    linksFrom(uriA, sharedCredentialsIndex()).some((link) => link.to.some((member) => memberMatchesHost(member, pageHost)))
  );
}

/** Domaine abandonne par son service, et celui ou ses comptes vivent desormais. */
export interface RenamedSite {
  /** Membre de la table qui designe l'adresse (`twitter.com`, `www.vistaprint.ca`). */
  readonly from: string;
  /** Nouveau domaine du service (`x.com`, `account.vistaprint.com`). */
  readonly to: string;
}

/**
 * Le service de cette adresse a-t-il quitte son domaine ? Table d'Apple, sens uniques marques `fromDomainsAreObsoleted`
 * seulement : un portail de connexion ou un domaine encore en service (`telegram.me`) n'est jamais un domaine abandonne.
 * `null` hors table. Sert a la Sante du coffre a proposer de mettre l'adresse de l'entree a jour.
 */
export function renamedSiteOf(uri: string): RenamedSite | null {
  const host = siteHostname(uri);
  if (host === null) return null;
  for (const link of linksFrom(uri, sharedCredentialsIndex())) {
    const from = link.obsolete ? link.from.find((member) => memberMatchesHost(member, host)) : undefined;
    if (from !== undefined && link.to.length > 0) return { from, to: link.to[0]! };
  }
  return null;
}
